import { marked } from 'marked';
import { apiClient } from '@/shared/api/client';

// Экспорт текстового результата (обычно Markdown) в разные форматы.
// PDF рендерится на сервере через агентский навык text-to-pdf (настоящий PDF,
// поддержка кириллицы), остальные форматы формируются на клиенте с рендерингом
// Markdown в HTML.
export type ExportFormat = 'md' | 'txt' | 'html' | 'doc' | 'pdf' | 'json';

export const EXPORT_FORMATS: { value: ExportFormat; label: string }[] = [
  { value: 'pdf', label: 'PDF (.pdf)' },
  { value: 'doc', label: 'Word (.doc)' },
  { value: 'html', label: 'HTML (.html)' },
  { value: 'md', label: 'Markdown (.md)' },
  { value: 'txt', label: 'Текст (.txt)' },
  { value: 'json', label: 'JSON (.json)' },
];

marked.setOptions({ gfm: true, breaks: true });

function renderMarkdown(md: string): string {
  return marked.parse(md, { async: false }) as string;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function htmlDocument(bodyHtml: string, title: string): string {
  return (
    `<!doctype html><html lang="ru"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>` +
    `<style>` +
    `body{font-family:'Segoe UI',Calibri,Arial,sans-serif;color:#1a1a1a;line-height:1.6;max-width:820px;margin:24px auto;padding:0 18px}` +
    `h1,h2,h3,h4{line-height:1.25;margin:1.2em 0 .5em}h1{font-size:1.8em}h2{font-size:1.45em}h3{font-size:1.2em}` +
    `p{margin:.6em 0}code{background:#f3f3f3;padding:.1em .3em;border-radius:4px;font-family:Consolas,monospace;font-size:.92em}` +
    `pre{background:#f6f8fa;padding:12px 14px;border-radius:8px;overflow:auto}pre code{background:none;padding:0}` +
    `blockquote{margin:.6em 0;padding:.2em 1em;border-left:4px solid #d0d0d0;color:#555}` +
    `table{border-collapse:collapse;margin:.8em 0;width:100%}th,td{border:1px solid #ccc;padding:6px 10px;text-align:left}th{background:#f3f3f3}` +
    `ul,ol{margin:.5em 0 .5em 1.4em}img{max-width:100%}a{color:#2563eb}` +
    `</style></head><body>${bodyHtml}</body></html>`
  );
}

function htmlToPlainText(html: string): string {
  const withBreaks = html
    .replace(/<\/(p|div|h[1-6]|tr|blockquote|pre|li)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ');
  const el = document.createElement('div');
  el.innerHTML = withBreaks;
  return (el.textContent || '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function sanitizeName(name: string): string {
  const base = (name || 'result').trim().replace(/[^\p{L}\p{N}_-]+/gu, '_').replace(/_+/g, '_');
  return base.replace(/^_|_$/g, '') || 'result';
}

// exportResult — сохраняет content в выбранном формате. Асинхронна: PDF строится
// на сервере. Бросает ошибку при сбое генерации PDF, чтобы UI мог её показать.
export async function exportResult(content: string, format: ExportFormat, baseName = 'result'): Promise<void> {
  const name = sanitizeName(baseName);
  switch (format) {
    case 'md':
      triggerDownload(new Blob([content], { type: 'text/markdown;charset=utf-8' }), `${name}.md`);
      return;
    case 'txt':
      triggerDownload(
        new Blob([htmlToPlainText(renderMarkdown(content))], { type: 'text/plain;charset=utf-8' }),
        `${name}.txt`,
      );
      return;
    case 'json':
      triggerDownload(
        new Blob([JSON.stringify({ content }, null, 2)], { type: 'application/json;charset=utf-8' }),
        `${name}.json`,
      );
      return;
    case 'html': {
      const doc = htmlDocument(renderMarkdown(content), name);
      triggerDownload(new Blob([doc], { type: 'text/html;charset=utf-8' }), `${name}.html`);
      return;
    }
    case 'doc': {
      // Word открывает HTML-документ с полным форматированием (заголовки, списки,
      // таблицы, жирный/курсив). BOM — чтобы кириллица читалась как UTF-8.
      const doc = htmlDocument(renderMarkdown(content), name);
      triggerDownload(new Blob(['\ufeff' + doc], { type: 'application/msword' }), `${name}.doc`);
      return;
    }
    case 'pdf': {
      const blob = await apiClient
        .post('tools/pdf', {
          json: { content, baseName: name },
          timeout: 180_000,
        })
        .blob();
      triggerDownload(blob, `${name}.pdf`);
      return;
    }
  }
}
